import React from 'react';
import {Platform, StyleProp, StyleSheet, TouchableOpacity, View, ViewStyle} from 'react-native';
import {useStyles} from '../../hooks/styles';
import {IStateProps, ServicesItem} from './interface';
import {DeviceSize, useDeviceSize} from '../../theme/helpers';
import {useTheme} from '../../theme';
import {Text} from '../text';
import {LoadableContent} from '../loadableContent';
import {images} from '../../../assets/icons';
import {useIsDesktop} from '../../hooks/useIsDesktop';
import {hexToRgbA} from '../../pipes';

interface ItemState {
  item: ServicesItem;
  onPress: IStateProps['onPress'];
}

const Images: React.FC<{item: ServicesItem; iconStyle: StyleProp<ViewStyle>}> = ({item, iconStyle}) => {
  const deviceSize = useDeviceSize();
  const theme = useTheme();
  const styles = useStyles(({mediaOnly}) => ({
    wrap: {
      position: 'absolute',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'flex-end',
      top: 0,
      bottom: 0,
      right: 0,
      left: 0,
    },
    iconWrap: {
      position: 'absolute',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-end',
      ...mediaOnly(
        DeviceSize.desktop,
        {
          top: 50,
          right: -30,
        },
        {
          top: 23,
          right: 3,
        },
      ),
    },
    icon: {
      ...mediaOnly(DeviceSize.desktop, {height: 'auto', width: 300}, {height: 75, width: 'auto'}),
    },
    tracery: {
      position: 'absolute',
      bottom: -1,
      left: 0,
      zIndex: -1,
    },
  }));

  const dataViewAttrs = {
    dataSet: {
      class: 'services-item-images-wrap',
    },
  };

  return (
    <View style={styles.wrap}>
      {deviceSize === DeviceSize.desktop ? <item.tracery style={StyleSheet.flatten(styles.tracery)} /> : null}
      <View style={[styles.iconWrap, iconStyle]} {...dataViewAttrs}>
        <item.icon width={75} height={70} style={StyleSheet.flatten(styles.icon)} theme={theme} />
      </View>
    </View>
  );
};

const Item: React.FC<ItemState> = ({item, onPress}) => {
  const styles = useStyles(({theme, addShadow, mediaOnly, deviceWidth, deviceSize}) => {
    const isIconHidden = deviceSize === DeviceSize.desktop && deviceWidth <= 1250;
    return {
      wrap: {
        flexGrow: 1,
        backgroundColor: theme.services.itemBackground,
        position: 'relative',
        overflow: 'hidden',
        ...addShadow({radius: 18, height: 2}),
        ...mediaOnly(
          DeviceSize.desktop,
          {
            paddingVertical: 50,
            paddingHorizontal: 30,
            borderRadius: 14,
            minHeight: 254,
            paddingRight: isIconHidden ? 30 : 270,
          },
          {paddingVertical: 12, paddingHorizontal: 16, borderRadius: 6, minHeight: 116, paddingRight: '30%'},
        ),
      },
      title: {
        ...mediaOnly(
          DeviceSize.desktop,
          {...theme.fonts.h3, marginBottom: 22},
          {...theme.fonts.action, marginBottom: 6},
        ),
      },
      text: {
        ...mediaOnly(DeviceSize.desktop, theme.fonts.h5Light, theme.fonts.bodySmallLight),
      },
      bodyText: {
        ...theme.fonts.h6Light,
      },
      images: {
        ...(isIconHidden ? {display: 'none'} : {}),
      },
    };
  });

  const dataSetAttrs = {
    dataSet: {
      class: 'services-item',
    },
  };

  return (
    <TouchableOpacity
      testID={`content-item-${item.key}`}
      style={styles.wrap}
      activeOpacity={Platform.OS === 'web' ? 1 : 0.5}
      onPress={() => onPress(item)}
      {...dataSetAttrs}>
      <Images iconStyle={styles.images} item={item} />
      <Text testID='card-title-id' style={styles.title}>
        {item.title}
      </Text>
      <Text testID='card-body-id' style={styles.text}>
        {item.body}
      </Text>
    </TouchableOpacity>
  );
};

export default ({onPress, items, useBackgroundPattern}: IStateProps) => {
  const isDesktop = useIsDesktop();
  const theme = useTheme();
  const styles = useStyles(({mediaOnly}) => ({
    itemsWrap: {
      ...mediaOnly(DeviceSize.desktop, {flexDirection: 'row', flexWrap: 'wrap', marginLeft: -20, marginRight: -20}),
    },
    item: {
      ...mediaOnly(
        DeviceSize.desktop,
        {paddingLeft: 20, paddingRight: 20, marginBottom: 40, width: '50%'},
        {marginBottom: 20},
      ),
    },
    backgroundPatternContainer: {
      position: 'absolute',
      zIndex: -1,
      width: 466,
      height: 479,
      right: -260,
      top: 73,
    },
    backgroundPattern: {
      marginLeft: 'auto',
      marginBottom: 'auto',
      alignSelf: 'flex-start',
      width: 466,
      height: 479,
      resizeMode: 'contain',
    },
  }));

  return (
    <LoadableContent loading={!items?.length}>
      <View style={styles.itemsWrap}>
        {!isDesktop && useBackgroundPattern && (
          <View style={styles.backgroundPatternContainer}>
            <images.ServiceDetailsPattern
              color={hexToRgbA(theme.color.shaddowBg, 0.15)}
              style={StyleSheet.flatten(styles.backgroundPattern)}
              hideForVisuallyImpaired
            />
          </View>
        )}
        {items.map((item, i) => (
          <View style={styles.item} key={i}>
            <Item onPress={onPress} item={item} />
          </View>
        ))}
      </View>
    </LoadableContent>
  );
};
